import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import './LocationManagement.css';
import {
  MapPin, Building2, Users, Search, RefreshCw, Ban, CheckCircle,
  ChevronDown, ChevronRight, Filter, Edit, AlertTriangle, Store
} from 'lucide-react';

const LocationManagement = () => {
  const [locations, setLocations] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [tierStats, setTierStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showMerchantLimitsModal, setShowMerchantLimitsModal] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [expandedMerchants, setExpandedMerchants] = useState({});

  // Fetch all locations
  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/locations');
      if (response.success) {
        setLocations(response.locations || []);
        setTierStats(response.tierStats || {});
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch merchants with location info
  const fetchMerchants = useCallback(async () => {
    try {
      const response = await api.get('/admin/locations/merchants/list');
      if (response.success) {
        setMerchants(response.merchants || []);
      }
    } catch (err) {
      console.error('Error fetching merchants:', err);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchMerchants();
  }, [fetchLocations, fetchMerchants]);

  // Toggle location status (suspend/unsuspend)
  const handleToggleStatus = async (locationId, currentStatus) => {
    try {
      setUpdating(true);
      const action = currentStatus === 'ACTIVE' ? 'suspend' : 'unsuspend';
      const response = await api.post(`/admin/locations/${locationId}/${action}`, {
        reason: `Admin ${action}ed location`
      });

      if (response.success) {
        setSuccess(`Location ${action}ed successfully`);
        fetchLocations();
      }
    } catch (err) {
      console.error('Error toggling location status:', err);
      setError(`Failed to ${currentStatus === 'ACTIVE' ? 'suspend' : 'unsuspend'} location`);
    } finally {
      setUpdating(false);
    }
  };

  // Update merchant location limits
  const handleUpdateMerchantLimits = async (merchantId, limits) => {
    try {
      setUpdating(true);
      const response = await api.put(`/admin/locations/merchants/${merchantId}/limits`, limits);

      if (response.success) {
        setSuccess('Merchant limits updated successfully');
        setShowMerchantLimitsModal(false);
        setSelectedMerchant(null);
        fetchMerchants();
      }
    } catch (err) {
      console.error('Error updating merchant limits:', err);
      setError('Failed to update merchant limits');
    } finally {
      setUpdating(false);
    }
  };

  // Toggle merchant expansion
  const toggleMerchantExpansion = (merchantId) => {
    setExpandedMerchants(prev => ({
      ...prev,
      [merchantId]: !prev[merchantId]
    }));
  };

  // Filter locations
  const filteredLocations = locations.filter(location => {
    const matchesSearch = searchTerm === '' ||
      location.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.storeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.code?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && location.adminStatus === 'ACTIVE') ||
      (statusFilter === 'suspended' && location.adminStatus === 'SUSPENDED');

    return matchesSearch && matchesStatus;
  });

  // Group locations by merchant
  const locationsByMerchant = filteredLocations.reduce((acc, location) => {
    const merchantId = location.merchantId || 'unknown';
    if (!acc[merchantId]) {
      acc[merchantId] = {
        merchantId,
        merchantName: location.merchantName || 'Unknown Merchant',
        storeName: location.storeName || 'Unknown Store',
        tier: location.tier || 'FREE',
        locations: []
      };
    }
    acc[merchantId].locations.push(location);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="location-management loading">
        <div className="spinner"></div>
        <p>Loading locations...</p>
      </div>
    );
  }

  return (
    <div className="location-management">
      {/* Header */}
      <div className="lm-header">
        <div className="lm-title">
          <MapPin size={28} />
          <div>
            <h1>Location Management</h1>
            <p>Manage merchant locations across the platform</p>
          </div>
        </div>

        <button className="lm-refresh-btn" onClick={fetchLocations} disabled={loading}>
          <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="lm-alert error">
          <AlertTriangle size={18} />
          {error}
          <button onClick={() => setError('')}>&times;</button>
        </div>
      )}
      {success && (
        <div className="lm-alert success">
          <CheckCircle size={18} />
          {success}
          <button onClick={() => setSuccess('')}>&times;</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="lm-stats">
        <div className="stat-card">
          <Building2 size={24} />
          <div>
            <span className="stat-value">{locations.length}</span>
            <span className="stat-label">Total Locations</span>
          </div>
        </div>
        <div className="stat-card">
          <Store size={24} />
          <div>
            <span className="stat-value">{Object.keys(locationsByMerchant).length}</span>
            <span className="stat-label">Merchants</span>
          </div>
        </div>
        <div className="stat-card active">
          <CheckCircle size={24} />
          <div>
            <span className="stat-value">
              {locations.filter(l => l.adminStatus === 'ACTIVE').length}
            </span>
            <span className="stat-label">Active</span>
          </div>
        </div>
        <div className="stat-card suspended">
          <Ban size={24} />
          <div>
            <span className="stat-value">
              {locations.filter(l => l.adminStatus === 'SUSPENDED').length}
            </span>
            <span className="stat-label">Suspended</span>
          </div>
        </div>
      </div>

      {/* Tier Distribution */}
      <div className="lm-tier-stats">
        <h3>Locations by Tier</h3>
        <div className="tier-bars">
          {Object.entries(tierStats).map(([tier, count]) => (
            <div key={tier} className="tier-bar">
              <span className="tier-name">{tier}</span>
              <div className="bar-container">
                <div
                  className="bar-fill"
                  style={{
                    width: `${(count / locations.length) * 100}%`,
                    backgroundColor: getTierColor(tier)
                  }}
                />
              </div>
              <span className="tier-count">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="lm-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search locations or merchants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Locations List by Merchant */}
      <div className="lm-merchants-list">
        {Object.values(locationsByMerchant).map((merchant) => (
          <div key={merchant.merchantId} className="merchant-group">
            <div
              className="merchant-row"
              onClick={() => toggleMerchantExpansion(merchant.merchantId)}
            >
              <div className="merchant-expand">
                {expandedMerchants[merchant.merchantId] ?
                  <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
              <div className="merchant-details">
                <h4>{merchant.storeName}</h4>
                <span className="merchant-name">{merchant.merchantName}</span>
                <span className="merchant-email">{merchant.locations[0]?.merchantEmail || ''}</span>
              </div>
              <div className="merchant-actions">
                <span className={`tier-badge ${merchant.tier.toLowerCase()}`}>
                  {merchant.tier}
                </span>
                <span className="location-count">
                  {merchant.locations.length} location{merchant.locations.length !== 1 ? 's' : ''}
                </span>
                <button
                  className="edit-limits-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMerchant(merchant);
                    setShowMerchantLimitsModal(true);
                  }}
                >
                  <Edit size={14} /> Limits
                </button>
              </div>
            </div>

            {expandedMerchants[merchant.merchantId] && (
              <div className="locations-list">
                {merchant.locations.map((location) => (
                  <div
                    key={location.id}
                    className={`location-item ${location.adminStatus?.toLowerCase()}`}
                  >
                    <div className="location-info">
                      <MapPin size={16} />
                      <div>
                        <h5>{location.name}</h5>
                        {location.code && <span className="location-code">{location.code}</span>}
                        {location.address && (
                          <p className="location-address">
                            {location.address}{location.city ? `, ${location.city}` : ''}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="location-badges">
                      {location.isPrimary && (
                        <span className="badge primary">Primary</span>
                      )}
                      <span className={`badge status ${location.adminStatus?.toLowerCase()}`}>
                        {location.adminStatus}
                      </span>
                    </div>

                    <div className="location-actions">
                      <button
                        className={`action-btn ${location.adminStatus === 'ACTIVE' ? 'suspend' : 'unsuspend'}`}
                        onClick={() => handleToggleStatus(location.id, location.adminStatus)}
                        disabled={updating}
                      >
                        {location.adminStatus === 'ACTIVE' ? (
                          <>
                            <Ban size={14} /> Suspend
                          </>
                        ) : (
                          <>
                            <CheckCircle size={14} /> Unsuspend
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {Object.keys(locationsByMerchant).length === 0 && (
          <div className="no-results">
            <Building2 size={48} />
            <p>No locations found matching your criteria</p>
          </div>
        )}
      </div>

      {/* Merchant Limits Modal */}
      {showMerchantLimitsModal && selectedMerchant && (
        <MerchantLimitsModal
          merchant={selectedMerchant}
          onClose={() => {
            setShowMerchantLimitsModal(false);
            setSelectedMerchant(null);
          }}
          onSave={handleUpdateMerchantLimits}
          updating={updating}
        />
      )}
    </div>
  );
};

// Helper function for tier colors
const getTierColor = (tier) => {
  const colors = {
    FREE: '#6b7280',
    PRO: '#3b82f6',
    PREMIUM: '#8b5cf6',
    BUSINESS: '#f59e0b',
    ENTERPRISE: '#10b981'
  };
  return colors[tier] || '#6b7280';
};

// Merchant Limits Modal Component
const MerchantLimitsModal = ({ merchant, onClose, onSave, updating }) => {
  const [limits, setLimits] = useState({
    locationLimitOverride: '',
    reason: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(merchant.merchantId, {
      locationLimit: limits.locationLimitOverride ? parseInt(limits.locationLimitOverride) : null,
      reason: limits.reason
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Update Location Limits</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="merchant-summary">
            <Store size={20} />
            <div>
              <strong>{merchant.storeName}</strong>
              <span>Current tier: {merchant.tier} ({merchant.locations.length} locations)</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Location Limit Override</label>
              <input
                type="number"
                min="1"
                max="100"
                placeholder={`Leave empty to use tier default`}
                value={limits.locationLimitOverride}
                onChange={(e) => setLimits({ ...limits, locationLimitOverride: e.target.value })}
              />
              <small>Enter a number to override the tier limit, or leave empty to use default</small>
            </div>

            <div className="form-group">
              <label>Reason for Override</label>
              <textarea
                placeholder="Explain why this override is being applied..."
                value={limits.reason}
                onChange={(e) => setLimits({ ...limits, reason: e.target.value })}
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-save" disabled={updating}>
                {updating ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LocationManagement;
